import React, { useEffect, useState } from "react";
import classes from "./Universities.module.scss";
import CustomImage from "../../CustomImage";
import { Card } from "react-bootstrap";
import useEmblaCarousel from "embla-carousel-react";
import useIsMobile from "../../../hooks/useIsMobile";
import fetchUtil from "../../../utils/fetchUtil";
import { getSlugFromCountry, getSlugFromCountryUniDetail } from "../../../utils/helpers";
import { countries, unicountries } from "../../../utils/constants";
import { getOnboardingOrFormCtaProps } from "../../../utils/applyCtaButtonProps";
import { useRouter } from "next/router";
import { persistSourceContext } from "../../../utils/b2c/sourceTracking";
import { PRIMARY_SOURCE_B2C_WIDGET } from "../../../utils/b2c/constants";

const Universities = ({ countryData }: { countryData: any }) => {
  const router = useRouter();
  const isMobile = useIsMobile(768);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [topUniversities, setTopUniversities] = useState<any>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const countrySlug = countryData?.name === "UK" ? "uk" : getSlugFromCountry(countryData?.name, countries);

  const goToPrev = () => emblaApi?.scrollPrev();
  const goToNext = () => emblaApi?.scrollNext();

  useEffect(() => {
    if (countryData?.name) {
      const countryName = countryData?.name === "UnitedKingdom" ? "UK" : countryData?.name;
      fetchUtil(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/universities?country=${countryName.replace(/ /g, "")}&_start=0&_limit=10&_sort=priority:ASC`,
        "GET",
        {},
        "",
        false,
      ).then((res) => setTopUniversities(res));
    }
  }, [countryData]);

  useEffect(() => {
    if (emblaApi && topUniversities.length > 0) {
      emblaApi.reInit();
    }
  }, [topUniversities, emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, topUniversities]);

  const applyClickHandler = (applyNowButtonProps: any, receivedData: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
    if (applyNowButtonProps?.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: receivedData?.nearestOffice ?? true,
        coachingVisible: receivedData?.lookingForCoaching ?? true,
      });
    }

    applyNowButtonProps?.onClick?.(e);
  };

  return (
    <div id="universities" className={classes["universities-container"]}>
      <h2 className={classes["universities-title"]}>{countryData?.topUniversities?.title ?? ""}</h2>
      <div
        className={classes["universities-description"]}
        dangerouslySetInnerHTML={{
          __html: countryData?.topUniversities?.description ?? "",
        }}
      ></div>

      {isMobile ? (
        // Mobile View
        <div className={`${classes["uni-cards-container"]} ${classes["uni-cards-container-mobile"]}`}>
          {/* EMBLA CAROUSEL */}
          <div className={classes.viewport} ref={emblaRef}>
            <div className={classes["uni-cards-slider-container"]}>
              {topUniversities.map((university: any) => {
                const slug = university.slug;
                const url = slug && university.country ? `/universities/${getSlugFromCountryUniDetail(university.country, unicountries)}/${slug}` : "";

                const applyCtaButtonProps = getOnboardingOrFormCtaProps(router, {
                  btnLink: university?.studyDestinationCard?.button2?.link,
                  formId: university?.studyDestinationCard?.button2?.formId,
                  toOnboardingEnabled: university?.studyDestinationCard?.button2?.toOnboardingEnabled,
                  sourceUrl: typeof window !== "undefined" ? window.location.pathname : "",
                });

                return (
                  <div className={classes.slide} key={university.id}>
                    <Card className={classes["bootstrap-card-mobile"]}>
                      <div className={classes.cardImageWrap}>
                        <CustomImage
                          src={university.universityLogo?.imageUrl || "/images/university-detail-gallery/university-dummy-logo.jpg"}
                          alt={university.name}
                          layout="responsive"
                          width={240}
                          height={80}
                        />
                      </div>
                      <div className={classes["separator"]}></div>
                      <div className={classes["card-content"]}>
                        <h3>{university.name}</h3>
                        <p className={classes["address-text"]}>{university.location}</p>
                        <p className={classes["visit-website-link"]}>{university.universityUrl}</p>
                      </div>

                      <div className={classes["card-btn-container"]}>
                        {slug && (
                          <button onClick={() => window.open(url, "_blank")} className={classes["know-more-btn"]}>
                            Know More
                          </button>
                        )}
                        <button
                          className={`${classes["apply-now-btn"]} ${applyCtaButtonProps.formClassName}`}
                          onClick={applyClickHandler(applyCtaButtonProps, university?.studyDestinationCard?.button2)}
                        >
                          {university?.studyDestinationCard?.button2?.title ?? "Apply Now"}
                        </button>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={classes["arrows-container"]}>
            {/* LEFT BUTTON */}
            <div className={`${classes["btcircle"]} ${!canScrollPrev ? classes["btcircle-disabled"] : ""}`} onClick={goToPrev}>
              <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt={"left-arrow"} />
            </div>
            {/* RIGHT BUTTON */}
            <div className={`${classes["btcircle"]} ${!canScrollNext ? classes["btcircle-disabled"] : ""}`} onClick={goToNext}>
              <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt={"right-arrow"} />
            </div>
          </div>
        </div>
      ) : (
        // Desktop View
        <>
          <div className={classes["uni-cards-container"]}>
            {/* EMBLA CAROUSEL */}
            <div className={classes.viewport} ref={emblaRef}>
              <div className={classes["uni-cards-slider-container"]}>
                {topUniversities.map((university: any) => {
                  const slug = university.slug;
                  const url = slug && university.country ? `/universities/${getSlugFromCountryUniDetail(university.country, unicountries)}/${slug}` : "";

                  const applyCtaButtonProps = getOnboardingOrFormCtaProps(router, {
                    btnLink: university?.studyDestinationCard?.button2?.link,
                    formId: university?.studyDestinationCard?.button2?.formId,
                    toOnboardingEnabled: university?.studyDestinationCard?.button2?.toOnboardingEnabled,
                    sourceUrl: typeof window !== "undefined" ? window.location.pathname : "",
                  });

                  return (
                    <div className={classes.slide} key={university.id}>
                      <Card className={classes["bootstrap-card"]}>
                        <div className={classes.cardImageWrap}>
                          <CustomImage
                            src={university.universityLogo?.imageUrl || "/images/university-detail-gallery/university-dummy-logo.jpg"}
                            alt={university.name}
                            layout="responsive"
                            width={240}
                            height={80}
                          />
                        </div>
                        <div className={classes["separator"]}></div>
                        <div className={classes["card-content"]}>
                          <h3>{university.name}</h3>
                          <p className={classes["address-text"]}>{university.location}</p>
                          <p className={classes["visit-website-link"]}>{university.universityUrl}</p>
                        </div>

                        <div className={classes["card-btn-container"]}>
                          {slug && (
                            <button onClick={() => window.open(url, "_blank")} className={classes["know-more-btn"]}>
                              Know More
                            </button>
                          )}

                          <button
                            className={`${classes["apply-now-btn"]} ${applyCtaButtonProps.formClassName}`}
                            onClick={applyClickHandler(applyCtaButtonProps, university?.studyDestinationCard?.button2)}
                          >
                            {university?.studyDestinationCard?.button2?.title ?? "Apply Now"}
                          </button>
                        </div>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={classes["arrows-container"]}>
            {/* LEFT BUTTON */}
            <div className={`${classes["btcircle"]} ${!canScrollPrev ? classes["btcircle-disabled"] : ""}`} onClick={goToPrev}>
              <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt={"left-arrow"} />
            </div>

            {/* RIGHT BUTTON */}
            <div className={`${classes["btcircle"]} ${!canScrollNext ? classes["btcircle-disabled"] : ""}`} onClick={goToNext}>
              <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt={"right-arrow"} />
            </div>
          </div>
        </>
      )}

      <button onClick={() => window.open(`/universities/${countrySlug}`, "_blank")} className={classes["view-more-universities-btn"]}>
        View More Universities
      </button>
    </div>
  );
};

export default Universities;
